import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  handleUpdateFields,
  getLoyaltyPointsItem,
  createLoyaltyPoints,
  updateLoyaltyPoints,
  LoyaltyPointsItem,
} from "store/liveOps/loyalty/points/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { WagerItem } from "store/liveOps/wagers/slice";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { Modal } from "components/Modal/Modal";
import styles from "./LiveOpsLoyaltyPointsEditModal.module.scss";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import Checkbox from "components/inputs/Checkbox/Checkbox";
import SwitchBtn from "components/buttons/SwitchBtn";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import FilterTabs from "components/FilterTabs";
import SearchDropdown, { Item } from "components/dropdowns/SearchDropdown/SearchDropdown";
import { getGameTypesService, getGamesByIdsService, getGamesListService } from "services/gamesService";
import { getProvidersByIdsService, getProvidersListService } from "services/providersService";
import { getGeoDropdownOptions } from "helpers/geoDropdown";

const WAGER_SCOPES = ["ALL", "CASINO", "SPORT", "GAME_TYPE", "PROVIDER", "GAME_SET"] as const;

const LoyaltyPointsModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector(
    (store) => store["loyalty-points"].isItemFetched,
  );
  const payload = useAppSelector((store) => store["loyalty-points"].payload);
  const currencies = useAppSelector((store) => store.settings.currencies);
  const geoList = useAppSelector((state) => state.settings.geoList);
  const isItemFetching = useAppSelector(
    (store) => store["loyalty-points"].isItemFetching,
  );
  const [wagerOptions, setWagerOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [gameTypes, setGameTypes] = useState<string[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<Item[]>([]);
  const [selectedGames, setSelectedGames] = useState<Item[]>([]);
  const [loyaltyLevelOptions, setLoyaltyLevelOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [loyaltyLevelsLoading, setLoyaltyLevelsLoading] = useState(false);
  const [loyaltyLevelsError, setLoyaltyLevelsError] = useState(false);
  const [loyaltyLevelsReloadKey, setLoyaltyLevelsReloadKey] = useState(0);

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const permissions = usePermissionHelper();
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canEdit = isEdit ? canUpdate : canCreate;

  const handleLoadWagers = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.wagersList}?limit=1000&offset=0`,
      );
      setWagerOptions(
        result.data.items.map((el: WagerItem) => {
          return {
            value: el.id,
            label: el.title,
          };
        }),
      );
    } catch {
      console.log("handleLoadWagers error");
    }
  };

  useEffect(() => {
    handleLoadWagers();
  }, []);

  useEffect(() => {
    if (entityId !== null && !isItemFetched) {
      dispatch(
        getLoyaltyPointsItem({
          id: entityId ?? "",
          type: searchParams.get("type") ?? "",
        }),
      );
    }
  }, [searchParams, isItemFetched]);

  const isLoaded = () => {
    if (entityId !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    if (!canEdit) return;
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  useEffect(() => {
    const type = searchParams.get("type");

    if (type !== null) {
      handleData("type", type);

      if (type === "wager" && !entityId) {
        dispatch(
          handleUpdateFields({
            type: "wager",
            multiplier: 1,
            scope: "ALL",
            isActive: 1,
            isRealBalanceAllowed: 1,
            isBonusBalanceAllowed: 0,
          }),
        );
      }
      if (type === "bet" && !entityId) {
        dispatch(
          handleUpdateFields({
            type: "bet",
            isRealBalanceAllowed: 1,
            isBonusBalanceAllowed: 0,
          }),
        );
      }
      if (type === "deposit" && !entityId) {
        dispatch(
          handleUpdateFields({
            type: "deposit",
            numberOfDeposit: 0,
            useDepositAmount: false,
            min: 0,
            max: 999999,
          }),
        );
      }
    }
  }, [searchParams, canEdit]);

  useEffect(() => {
    if (searchParams.get("type") !== "wager") return;
    getGameTypesService().then((types: string[]) => {
      setGameTypes(Array.from(new Set(types.filter(Boolean))));
    });
  }, [searchParams]);

  useEffect(() => {
    if (!["deposit", "wager"].includes(searchParams.get("type") || "")) return;
    let cancelled = false;
    setLoyaltyLevelsLoading(true);
    setLoyaltyLevelsError(false);
    const loadLevels = async () => {
      try {
        const limit = 1000;
        let offset = 0;
        let totalPages = 1;
        const levels: {
          id?: number;
          levelNumber?: string | number;
          title?: string;
        }[] = [];
        while (offset < totalPages) {
          const result = await instance.post(apiRoutes.loyaltyLevels, {
            limit,
            offset,
            isActive: -1,
            order: { levelNumber: "asc" },
          });
          if (cancelled) return;
          if (Array.isArray(result.data?.items)) {
            levels.push(...result.data.items);
          }
          totalPages = Math.max(1, Number(result.data?.totalPages) || 1);
          offset += 1;
        }
        const uniqueLevels = Array.from(
          new Map(levels.map((level) => [Number(level.id), level])).values(),
        );
        setLoyaltyLevelOptions(
          uniqueLevels
            .filter(
              (level) =>
                Number.isInteger(Number(level.id)) &&
                Number(level.id) > 0 &&
                Number.isFinite(Number(level.levelNumber)) &&
                String(level.title || "").trim() !== "",
            )
            .map((level) => ({
              value: Number(level.id),
              label: `Level ${level.levelNumber} — ${level.title}`,
            })),
        );
      } catch {
        if (cancelled) return;
        setLoyaltyLevelsError(true);
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityFetchError"),
            type: NotificationTypes.error,
          }),
        );
      } finally {
        if (!cancelled) setLoyaltyLevelsLoading(false);
      }
    };
    loadLevels();
    return () => {
      cancelled = true;
    };
  }, [searchParams, loyaltyLevelsReloadKey, dispatch, t]);

  useEffect(() => {
    if (payload?.scope !== "PROVIDER" || !payload.providerId) {
      setSelectedProvider([]);
      return;
    }
    let cancelled = false;
    getProvidersByIdsService([payload.providerId]).then((items) => {
      if (cancelled) return;
      const match = items.find((item: { id?: number; title?: string }) => item.id === payload.providerId);
      setSelectedProvider([
        {
          value: payload.providerId as number,
          label: String(match?.title || `#${payload.providerId}`),
        },
      ]);
    });
    return () => {
      cancelled = true;
    };
  }, [payload?.scope, payload?.providerId]);

  useEffect(() => {
    const ids = payload?.gameIds || [];
    if (payload?.scope !== "GAME_SET" || ids.length === 0) {
      setSelectedGames([]);
      return;
    }
    let cancelled = false;
    getGamesByIdsService(ids).then((items) => {
      if (cancelled) return;
      const byId = new Map(
        items.map((item: { id?: number; title?: string }) => [item.id, item.title || ""]),
      );
      setSelectedGames(
        ids.map((id) => ({
          value: id,
          label: String(byId.get(id) || `#${id}`),
        })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [payload?.scope, (payload?.gameIds || []).join(",")]);

  const handleWagerScope = (scope: string | number) => {
    if (!canEdit) return;
    const nextScope = String(scope);
    dispatch(
      handleUpdateFields({
        scope: nextScope,
        gameType: nextScope === "GAME_TYPE" ? payload?.gameType ?? null : null,
        providerId: nextScope === "PROVIDER" ? payload?.providerId ?? null : null,
        gameIds: nextScope === "GAME_SET" ? payload?.gameIds ?? [] : [],
      }),
    );
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (!canEdit) return;
    if (!payload) return;

    formErrors.resetErrors();

    const currencyIsRequired = payload.type !== "signUp";
    const currencyIsInvalid =
      currencyIsRequired
      && (payload.currency == null || String(payload.currency).trim() === "");
    const hasLoyaltyOverride =
      (payload.type === "deposit" || payload.type === "wager")
      && payload.loyaltyLevelId != null;
    const loyaltyMultiplier = Number(payload.loyaltyMultiplier);
    const loyaltyMultiplierIsInvalid =
      hasLoyaltyOverride
      && (
        !Number.isFinite(loyaltyMultiplier)
        || loyaltyMultiplier < 1
      );

    if (currencyIsInvalid || loyaltyMultiplierIsInvalid) {
      if (currencyIsInvalid) {
        formErrors.setFieldError("currency", "requiredField");
      }
      if (loyaltyMultiplierIsInvalid) {
        formErrors.setFieldError("loyaltyMultiplier", "InvalidFormat");
      }
      dispatch(
        showNotification({
          title: t("error"),
          message:
            loyaltyMultiplierIsInvalid
              ? t("loyaltyMultiplierMinTooltip")
              : t("checkHighlightedFields"),
          type: NotificationTypes.error,
        }),
      );
      return;
    }

    if (payload.type === "deposit") {
      const hasNumberOfDeposit =
        payload.numberOfDeposit != null
        && String(payload.numberOfDeposit).trim() !== "";
      const numberOfDeposit = Number(payload.numberOfDeposit);
      if (
        !hasNumberOfDeposit
        || !Number.isInteger(numberOfDeposit)
        || numberOfDeposit < 0
      ) {
        formErrors.setFieldError("numberOfDeposit", "InvalidFormat");
        return;
      }
      if (payload.useDepositAmount) {
        const depositMultiplier = Number(payload.depositMultiplier);
        if (!Number.isFinite(depositMultiplier) || depositMultiplier < 1) {
          formErrors.setFieldError("depositMultiplier", "InvalidFormat");
          return;
        }
      } else {
        const points = Number(payload.points);
        const hasMin = payload.min != null && String(payload.min).trim() !== "";
        const hasMax = payload.max != null && String(payload.max).trim() !== "";
        const min = Number(payload.min);
        const max = Number(payload.max);
        if (!Number.isInteger(points) || points <= 0) {
          formErrors.setFieldError("points", "InvalidFormat");
          return;
        }
        if (!hasMin || !Number.isFinite(min) || min < 0) {
          formErrors.setFieldError("min", "InvalidFormat");
          return;
        }
        if (!hasMax || !Number.isFinite(max) || max < min) {
          formErrors.setFieldError("max", "InvalidFormat");
          return;
        }
      }
    }

    if (
      (payload.type === "deposit" || payload.type === "wager")
      && payload.loyaltyLevelId != null
    ) {
      const loyaltyLevelId = Number(payload.loyaltyLevelId);
      if (
        !Number.isInteger(loyaltyLevelId) ||
        loyaltyLevelId <= 0 ||
        (
          !loyaltyLevelsLoading
          &&
          !loyaltyLevelsError
          && !loyaltyLevelOptions.some((option) => option.value === loyaltyLevelId)
        )
      ) {
        formErrors.setFieldError("loyaltyLevelId", "InvalidFormat");
        return;
      }
    }

    try {
      await dispatch(
        isEdit
          ? updateLoyaltyPoints(payload as LoyaltyPointsItem)
          : createLoyaltyPoints(payload as LoyaltyPointsItem),
      ).unwrap();

      dispatch(
        showNotification({
          title: t("success"),
          message: isEdit ? t("entityUpdateSuccess") : t("entityCreateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
      const overlap = (error as ApiErrorPayload)?.fieldErrors?.find(
        (item) => item.code === "WagerPointGameSetOverlap",
      );
      if (overlap) {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("errors.WagerPointGameSetOverlap", {
              currency: overlap.meta?.currency || payload.currency || "",
            }),
            type: NotificationTypes.error,
          }),
        );
      }
    }
  };

  const renderDepositLoyaltyOverrideFields = () => (
    <>
      <div className="loyalty-points-form-box--item">
        <DefaultDropdown
          disabled={!canEdit || loyaltyLevelsLoading || loyaltyLevelsError}
          loading={loyaltyLevelsLoading}
          value={payload?.loyaltyLevelId ?? ""}
          selectedLabel={
            payload?.loyaltyLevelId != null
              ? loyaltyLevelOptions.find(
                  (option) => option.value === Number(payload.loyaltyLevelId),
                )?.label ?? `Level #${payload.loyaltyLevelId}`
              : undefined
          }
          placeholder={t("none")}
          label={t("loyaltyLevel")}
          options={[{ value: "", label: t("none") }, ...loyaltyLevelOptions]}
          onSelect={(value) => {
            formErrors.clearFieldsErrors([
              "loyaltyLevelId",
              "loyaltyMultiplier",
            ]);
            if (value === "") {
              dispatch(
                handleUpdateFields({
                  loyaltyLevelId: null,
                  loyaltyMultiplier: null,
                }),
              );
              return;
            }
            dispatch(
              handleUpdateFields({
                loyaltyLevelId: Number(value),
                loyaltyMultiplier: payload?.loyaltyMultiplier ?? null,
              }),
            );
          }}
          hasError={!!formErrors.errors.loyaltyLevelId}
        />
        {loyaltyLevelsError && (
          <Button
            isSecondary
            isSmall
            txt={t("retry")}
            handleClick={() =>
              setLoyaltyLevelsReloadKey((current) => current + 1)
            }
          />
        )}
      </div>
      {payload?.loyaltyLevelId != null && (
        <div className="loyalty-points-form-box--item">
          <InnerInput
            disabled={!canEdit}
            value={payload?.loyaltyMultiplier?.toString() ?? ""}
            placeholder={t("loyaltyMultiplier")}
            label={t("loyaltyMultiplier")}
            tooltipText={t("loyaltyMultiplierMinTooltip")}
            tooltipPosition="top-left"
            type="number"
            inputMode="decimal"
            min={1}
            isRequired
            id={`${payload.type}-loyalty-multiplier`}
            onChange={(value) => {
              handleData("loyaltyMultiplier", value);
              formErrors.clearFieldError("loyaltyMultiplier");
            }}
            hasError={!!formErrors.errors.loyaltyMultiplier}
          />
        </div>
      )}
    </>
  );

  return (
    <Modal onClose={handleCloseClick} size={payload?.type === "wager" ? "md" : "sm"}>
      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Header>{t(`${payload?.type}Points`)}</Modal.Header>

          <Modal.Content
            className={styles.liveopsLoyaltyPointsEditModalContentWrapper}
          >
            <div
              className={
                payload?.type === "deposit"
                  ? `loyalty-points-content ${styles.depositContent}`
                  : "loyalty-points-content"
              }
            >
              <div className="loyalty-points-form-box--item">
                <DefaultDropdown
                  disabled
                  value={payload?.type}
                  placeholder={t("type")}
                  label={t("type")}
                  isBot
                  options={[
                    {
                      value: "signUp",
                      label: t("signUp"),
                    },
                    {
                      value: "deposit",
                      label: t("deposit"),
                    },
                    {
                      value: "bet",
                      label: t("bet"),
                    },
                    {
                      value: "wager",
                      label: t("wager"),
                    },
                  ]}
                  onSelect={(e) => {
                    handleData("type", e);
                    formErrors.clearFieldError("type");
                  }}
                  hasError={!!formErrors.errors.type}
                />
              </div>
              {payload?.type === "signUp" ? (
                <div className="loyalty-points-form-box--item">
                  <DefaultDropdown
                    disabled={!canEdit}
                    value={payload?.country}
                    placeholder={t("country")}
                    label={t("country")}
                    isBot
                    options={getGeoDropdownOptions(geoList)}
                    onSelect={(e) => {
                      handleData("country", e);
                      formErrors.clearFieldError("country");
                    }}
                    hasError={!!formErrors.errors.country}
                  />
                </div>
              ) : (
                <div className="loyalty-points-form-box--item">
                  <DefaultDropdown
                    disabled={!canEdit}
                    value={payload?.currency}
                    placeholder={t("currency")}
                    label={t("currency")}
                    isRequired
                    options={currencies.map((el) => {
                      return {
                        value: el,
                        label: el,
                      };
                    })}
                    onSelect={(e) => {
                      handleData("currency", e);
                      formErrors.clearFieldError("currency");
                    }}
                    hasError={!!formErrors.errors.currency}
                  />
                </div>
              )}
              {payload?.type === "signUp" && (
                <>
                  <div className="loyalty-points-form-box--item">
                    <InnerInput
                      disabled={!canEdit}
                      value={payload?.points?.toString() ?? ""}
                      placeholder={t("points")}
                      label={t("points")}
                      type="number"
                      id="loyalty-level-points"
                      onChange={(e) => {
                        handleData("points", e);
                        formErrors.clearFieldError("points");
                      }}
                      hasError={!!formErrors.errors.points}
                    />
                  </div>
                </>
              )}
              {payload?.type === "deposit" && (
                <>
                  <div
                    className={`loyalty-points-form-box--item row ${styles.depositRuleRow}`}
                  >
                    <div className={styles.fieldWithHelper}>
                      <InnerInput
                        disabled={!canEdit}
                        value={payload?.numberOfDeposit?.toString() ?? ""}
                        placeholder={t("numberOfDeposit")}
                        label={t("numberOfDeposit")}
                        type="number"
                        inputMode="numeric"
                        min={0}
                        id="loyalty-level-numberOfDeposit"
                        onChange={(e) => {
                          handleData("numberOfDeposit", e);
                          formErrors.clearFieldError("numberOfDeposit");
                        }}
                        hasError={!!formErrors.errors.numberOfDeposit}
                      />
                      <span className={styles.helperText}>{t("anyDepositHelper")}</span>
                    </div>
                    <InnerInput
                      disabled={!canEdit || !!payload?.useDepositAmount}
                      value={payload?.points?.toString() ?? ""}
                      placeholder={t("points")}
                      label={t("points")}
                      type="number"
                      id="loyalty-level-points"
                      onChange={(e) => {
                        handleData("points", e);
                        formErrors.clearFieldError("points");
                      }}
                      hasError={!!formErrors.errors.points}
                    />
                  </div>
                  <div
                    className={`loyalty-points-form-box--item row ${styles.depositModeRow}`}
                  >
                    <div className={styles.depositAmountToggle}>
                      <Checkbox
                        disabled={!canEdit}
                        id="use-deposit-amount"
                        txt={t("useDepositAmount")}
                        value={!!payload?.useDepositAmount}
                        onCheck={(value) => {
                          dispatch(
                            handleUpdateFields({
                              useDepositAmount: value,
                              depositMultiplier: value
                                ? payload?.depositMultiplier ?? 1
                                : null,
                            }),
                          );
                          formErrors.clearFieldsErrors([
                            "depositMultiplier",
                            "points",
                            "min",
                            "max",
                          ]);
                        }}
                      />
                    </div>
                    <div className={styles.depositMultiplier}>
                      <InnerInput
                        disabled={!canEdit || !payload?.useDepositAmount}
                        value={payload?.depositMultiplier?.toString() ?? ""}
                        label={t("multiplier")}
                        type="number"
                        inputMode="decimal"
                        min={1}
                        isRequired={!!payload?.useDepositAmount}
                        id="deposit-amount-multiplier"
                        onChange={(value) => {
                          handleData("depositMultiplier", value);
                          formErrors.clearFieldError("depositMultiplier");
                        }}
                        hasError={!!formErrors.errors.depositMultiplier}
                      />
                    </div>
                  </div>
                  <div
                    className={`loyalty-points-form-box--item row ${styles.depositRuleRow}`}
                  >
                    <InnerInput
                      disabled={!canEdit || !!payload?.useDepositAmount}
                      value={payload?.min?.toString() ?? ""}
                      placeholder={t("min")}
                      label={t("min")}
                      type="number"
                      id="loyalty-level-min"
                      onChange={(e) => {
                        handleData("min", e);
                        formErrors.clearFieldError("min");
                      }}
                      hasError={!!formErrors.errors.min}
                    />
                    <InnerInput
                      disabled={!canEdit || !!payload?.useDepositAmount}
                      value={payload?.max?.toString() ?? ""}
                      placeholder={t("max")}
                      label={t("max")}
                      type="number"
                      id="loyalty-level-max"
                      onChange={(e) => {
                        handleData("max", e);
                        formErrors.clearFieldError("max");
                      }}
                      hasError={!!formErrors.errors.max}
                    />
                  </div>
                  {renderDepositLoyaltyOverrideFields()}
                </>
              )}
              {payload?.type === "bet" && (
                <>
                  <div className="loyalty-points-form-box--item row">
                    <InnerInput
                      disabled={!canEdit}
                      value={payload?.min?.toString() ?? ""}
                      placeholder={t("min")}
                      label={t("min")}
                      type="number"
                      id="loyalty-level-min"
                      onChange={(e) => {
                        handleData("min", e);
                        formErrors.clearFieldError("min");
                      }}
                      hasError={!!formErrors.errors.min}
                    />

                    <InnerInput
                      disabled={!canEdit}
                      value={payload?.max?.toString() ?? ""}
                      placeholder={t("max")}
                      label={t("max")}
                      type="number"
                      id="loyalty-level-max"
                      onChange={(e) => {
                        handleData("max", e);
                        formErrors.clearFieldError("max");
                      }}
                      hasError={!!formErrors.errors.max}
                    />
                  </div>

                  <div className="loyalty-points-form-box--item">
                    <InnerInput
                      disabled={!canEdit}
                      value={payload?.points?.toString() ?? ""}
                      placeholder={t("points")}
                      label={t("points")}
                      type="number"
                      id="loyalty-level-points"
                      onChange={(e) => {
                        handleData("points", e);
                        formErrors.clearFieldError("points");
                      }}
                      hasError={!!formErrors.errors.points}
                    />
                  </div>

                  <div className="loyalty-points-form-box--item row">
                    <Checkbox
                      disabled={!canEdit}
                      id="real-balance-allowed"
                      txt={t("realBalanceAllowed")}
                      value={!!payload?.isRealBalanceAllowed}
                      onCheck={(value) => {
                        handleData("isRealBalanceAllowed", value ? 1 : 0);
                      }}
                    />

                    <Checkbox
                      disabled={!canEdit}
                      id="bonus-balance-allowed"
                      txt={t("bonusBalanceAllowed")}
                      value={!!payload?.isBonusBalanceAllowed}
                      onCheck={(value) => {
                        handleData("isBonusBalanceAllowed", value ? 1 : 0);
                      }}
                    />
                  </div>
                </>
              )}
              {payload?.type === "wager" && (
                <>
                  <div className="loyalty-points-form-box--item">
                    <FilterTabs
                      isInner
                      disabled={!canEdit}
                      label={t("scope")}
                      value={payload?.scope || "ALL"}
                      options={WAGER_SCOPES.map((scope) => ({
                        value: scope,
                        label: t(
                          scope === "ALL"
                            ? "all"
                            : scope === "CASINO"
                              ? "casino"
                              : scope === "SPORT"
                                ? "sport"
                                : scope === "GAME_TYPE"
                                  ? "gameType"
                                  : scope === "PROVIDER"
                                    ? "provider"
                                    : "games",
                        ),
                      }))}
                      onSelect={handleWagerScope}
                    />
                  </div>
                  {payload?.scope === "GAME_TYPE" && (
                    <div className="loyalty-points-form-box--item">
                      <DefaultDropdown
                        disabled={!canEdit}
                        label={t("gameType")}
                        placeholder={t("gameType")}
                        value={payload?.gameType || ""}
                        options={gameTypes.map((type) => ({ value: type, label: type }))}
                        onSelect={(value) => {
                          dispatch(handleUpdateFields({ gameType: String(value) }));
                        }}
                      />
                    </div>
                  )}
                  {payload?.scope === "PROVIDER" && (
                    <div className="loyalty-points-form-box--item">
                      <SearchDropdown
                        mode="single"
                        disabled={!canEdit}
                        fetchService={getProvidersListService}
                        label={t("provider")}
                        searchQuery="title"
                        defaultValues={selectedProvider}
                        onSelect={(item) => {
                          if ((item as Item & { removed?: boolean }).removed) {
                            dispatch(handleUpdateFields({ providerId: null }));
                            return;
                          }
                          dispatch(handleUpdateFields({ providerId: Number(item.value) }));
                        }}
                      />
                    </div>
                  )}
                  {payload?.scope === "GAME_SET" && (
                    <div className="loyalty-points-form-box--item">
                      <SearchDropdown
                        mode="multiple"
                        disabled={!canEdit}
                        hasError={!!formErrors.errors.gameIds}
                        fetchService={getGamesListService}
                        label={t("games")}
                        searchQuery="gameTitle"
                        defaultValues={selectedGames}
                        onSelect={(item) => {
                          formErrors.clearFieldError("gameIds");
                          const current = payload?.gameIds || [];
                          const gameId = Number(item.value);
                          if ((item as Item & { removed?: boolean }).removed) {
                            dispatch(
                              handleUpdateFields({
                                gameIds: current.filter((id) => id !== gameId),
                              }),
                            );
                            return;
                          }
                          if (!current.includes(gameId)) {
                            dispatch(handleUpdateFields({ gameIds: [...current, gameId] }));
                          }
                        }}
                      />
                    </div>
                  )}
                  <div className="loyalty-points-form-box--item">
                    <InnerInput
                      disabled={!canEdit}
                      value={payload?.multiplier?.toString() ?? ""}
                      placeholder={t("multiplier")}
                      label={t("multiplier")}
                      type="number"
                      id="wager-base-multiplier"
                      onChange={(e) => {
                        handleData("multiplier", e);
                        formErrors.clearFieldError("multiplier");
                      }}
                      hasError={!!formErrors.errors.multiplier}
                    />
                  </div>
                  <div className="loyalty-points-form-box--item">
                    <DefaultDropdown
                      disabled={!canEdit || loyaltyLevelsLoading || loyaltyLevelsError}
                      loading={loyaltyLevelsLoading}
                      value={payload?.loyaltyLevelId ?? ""}
                      selectedLabel={
                        payload?.loyaltyLevelId != null
                          ? `Level #${payload.loyaltyLevelId}`
                          : undefined
                      }
                      placeholder={t("none")}
                      label={t("loyaltyLevel")}
                      options={[
                        { value: "", label: t("none") },
                        ...loyaltyLevelOptions,
                      ]}
                      onSelect={(value) => {
                        formErrors.clearFieldsErrors([
                          "loyaltyLevelId",
                          "loyaltyMultiplier",
                        ]);
                        if (value === "") {
                          dispatch(
                            handleUpdateFields({
                              loyaltyLevelId: null,
                              loyaltyMultiplier: null,
                            }),
                          );
                          return;
                        }
                        dispatch(
                          handleUpdateFields({
                            loyaltyLevelId: Number(value),
                            loyaltyMultiplier: payload?.loyaltyMultiplier ?? null,
                          }),
                        );
                      }}
                      hasError={!!formErrors.errors.loyaltyLevelId}
                    />
                    {loyaltyLevelsError && (
                      <Button
                        isSecondary
                        isSmall
                        txt={t("retry")}
                        handleClick={() =>
                          setLoyaltyLevelsReloadKey((current) => current + 1)
                        }
                      />
                    )}
                  </div>
                  {payload?.loyaltyLevelId != null && (
                    <div className="loyalty-points-form-box--item">
                      <InnerInput
                        disabled={!canEdit}
                        value={payload?.loyaltyMultiplier?.toString() ?? ""}
                        placeholder={t("loyaltyMultiplier")}
                        label={t("loyaltyMultiplier")}
                        tooltipText={t("loyaltyMultiplierMinTooltip")}
                        tooltipPosition="top-left"
                        type="number"
                        inputMode="decimal"
                        min={1}
                        isRequired
                        id="wager-loyalty-multiplier"
                        onChange={(value) => {
                          handleData("loyaltyMultiplier", value);
                          formErrors.clearFieldError("loyaltyMultiplier");
                        }}
                        hasError={!!formErrors.errors.loyaltyMultiplier}
                      />
                    </div>
                  )}
                  <div className="loyalty-points-form-box--item row">
                    <Checkbox
                      disabled={!canEdit}
                      id="real-balance-allowed"
                      txt={t("realBalanceAllowed")}
                      value={!!payload?.isRealBalanceAllowed}
                      onCheck={(value) => {
                        handleData("isRealBalanceAllowed", value ? 1 : 0);
                      }}
                    />

                    <Checkbox
                      disabled={!canEdit}
                      id="bonus-balance-allowed"
                      txt={t("bonusBalanceAllowed")}
                      value={!!payload?.isBonusBalanceAllowed}
                      onCheck={(value) => {
                        handleData("isBonusBalanceAllowed", value ? 1 : 0);
                      }}
                    />
                  </div>
                  <div className="loyalty-points-form-box--item">
                    <SwitchBtn
                      disabled={!canEdit}
                      activeTxt={t("active")}
                      notActiveTxt={t("hidden")}
                      isActive={!!payload?.isActive}
                      handleClick={() => {
                        handleData("isActive", payload?.isActive ? 0 : 1);
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </Modal.Content>

          {canEdit && (
            <Modal.Footer>
              <Button
                isSecondary
                txt={t("cancel")}
                handleClick={handleCloseClick}
              />

              <Button
                isPrimary
                handleClick={handleApply}
                txt={t("save")}
                isLoad={isItemFetching}
                disabled={loyaltyLevelsLoading}
              />
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default LoyaltyPointsModal;
